import { useEffect, useState } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { User } from '../../../../clients/quipli-service/user/user';
import { Typography } from '../../../../components/typography/typography';
import { Field, FieldLabel } from '../../../../components/field/field';
import { ApiError } from '../../../../clients/axios-instance';
import { Spinner } from '../../../../components/spinner/spinner';
import { toast } from '../../../../util/toasts';
import { OnAccount, OnAccountStatus } from '../../../../clients/quipli-service/payment/on-account/on-account';
import { Card } from '../../../../components/card/card';
import { CycleBillingTerms } from '../../../../clients/quipli-service/order/order';
import { Duration } from 'luxon';
import { DurationInput } from '../../../../components/form/duration-input';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { CardPaymentMethodInput } from '../../../payment/card-payment-method-input';
import { ConfirmModal } from '../../../../components/modal/confirm-modal';
import { Button } from '../../../../components/form/button';

export const PaymentMethods = ({ user }: { user: User }) => {
  const { getUserBankDetail, getMultipleConfigurations } = useAPI();
  const [confRentOnAccountEnable, setConfRentOnAccountEnable] = useState<boolean>(false);
  const [onAccountInfo, setOnAccountInfo] = useState<OnAccount>();
  const [cycleBillingEnabled, setCycleBillingEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const bankDetail = await getUserBankDetail(user.id);
      setOnAccountInfo(bankDetail);
    } catch (error) {
      console.log(error);
    }
    try {
      const config = await getMultipleConfigurations(['CONF_RENT_ON_ACCOUNT', 'CONF_CYCLE_BILLING']);
      setConfRentOnAccountEnable(config.find((_) => _.variable == 'CONF_RENT_ON_ACCOUNT')?.value === '1');
      setCycleBillingEnabled(config.find((_) => _.variable == 'CONF_CYCLE_BILLING')?.value === '1');
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  return isLoading ? (
    <Spinner />
  ) : (
    <div className="tab-section mb-4 flex flex-column gap-4">
      {confRentOnAccountEnable && (
        <>
          <div>
            <div className="tab-title">
              <Typography type="text-xl" weight="semi-bold">
                On Account Info
              </Typography>
            </div>
            <Card className="p-4 flex flex-column gap-4 w-fit min-w-[50%]">
              <OnAccountForm user={user} onAccount={onAccountInfo} onSubmit={fetchPayments} />
            </Card>
          </div>
          {cycleBillingEnabled && (
            <div>
              <div className="tab-title">
                <Typography type="text-xl" weight="semi-bold">
                  Cycle Billing Terms
                </Typography>
              </div>
              <Card className="p-4 flex flex-column gap-4 w-fit min-w-[50%]">
                <CycleBillingTermsForm user={user} />
              </Card>
            </div>
          )}
        </>
      )}
      <div>
        <div className="tab-title">
          <Typography type={'text-xl'} weight={'semi-bold'}>
            Credit Cards
          </Typography>
        </div>
        <Card className="p-4 flex flex-column gap-4 w-fit min-w-[50%]">
          <CardPaymentMethodInput
            userId={user.id}
            setPaymentMethod={() => {}}
            selectMode={false}
          ></CardPaymentMethodInput>
        </Card>
      </div>
    </div>
  );
};

export const CycleBillingTermsForm = ({ user, onSubmit }: { user: User; onSubmit?: () => void }) => {
  const { updateUserCycleBillingTerms } = useAPI();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cycleBillingTerms, setCycleBillingTerms] = useState<CycleBillingTerms>(user.cycleBillingTerms);

  const isBtnDisabled = (): boolean =>
    cycleBillingTerms.cycleLength.as('milliseconds') <= 0 ||
    cycleBillingTerms.paymentPeriod.days < 0 ||
    !cycleBillingTerms.cycleInvoiceDay ||
    !(
      cycleBillingTerms.cycleLength.months === 1 ||
      cycleBillingTerms.cycleLength.minus({ days: cycleBillingTerms.cycleInvoiceDay }).as('milliseconds') >= 0
    );

  const submit = () => {
    setIsLoading(true);
    updateUserCycleBillingTerms(user.id, cycleBillingTerms)
      .then(() => {
        toast("User's cycle billing terms have been updated successfully", 'success');
        onSubmit?.();
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : "Error updating user's cycle billing terms";
        toast(errMsg, 'error');
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setCycleBillingTerms(user.cycleBillingTerms);
  }, [user]);

  const termsAreInvalid =
    cycleBillingTerms.cycleLength.months !== 1 &&
    cycleBillingTerms.cycleLength.minus({ days: cycleBillingTerms.cycleInvoiceDay }).as('milliseconds') < 0;

  return (
    <>
      <div className="flex gap-8">
        <div className="flex items-end gap-2">
          <div>
            <FieldLabel label="Cycle Bill Length" isRequired={true} />
            <DurationInput
              value={cycleBillingTerms.cycleLength}
              onChange={(cycleLength) =>
                setCycleBillingTerms({
                  ...cycleBillingTerms,
                  cycleLength,
                })
              }
              allowedUnits={['months', 'weeks', 'days']}
            />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <Field
            type="number"
            placeholder="30"
            label="Payment Terms"
            isRequired={true}
            value={cycleBillingTerms.paymentPeriod.days}
            onChange={(paymentPeriod) =>
              setCycleBillingTerms({
                ...cycleBillingTerms,
                paymentPeriod: Duration.fromObject({ days: paymentPeriod }),
              })
            }
          />
          <span className="">days</span>
        </div>
      </div>

      <div className="flex items-end mt-3 gap-2">
        <Field
          invalid={cycleBillingTerms.cycleInvoiceDay > cycleBillingTerms.cycleLength.days!}
          type="number"
          placeholder="28"
          label="Day of Cycle Invoice Sent to Customer"
          isRequired={true}
          value={cycleBillingTerms.cycleInvoiceDay}
          onChange={(cycleInvoiceDay) => setCycleBillingTerms({ ...cycleBillingTerms, cycleInvoiceDay })}
        />

        {cycleBillingTerms.cycleLength.months === 1 && (
          <span className="py-2">
            <Checkbox
              type="white"
              isChecked={cycleBillingTerms.isDayOfMonth}
              onChange={() =>
                setCycleBillingTerms({
                  ...cycleBillingTerms,
                  isDayOfMonth: !cycleBillingTerms.isDayOfMonth,
                })
              }
              label={
                <Typography type="text-md" className="text-grey-700">
                  As Day of Month
                </Typography>
              }
            />
          </span>
        )}
      </div>
      {termsAreInvalid && (
        <Typography type="text-xs" className="text-error-500">
          * Invoice day cannot be greater than cycle length
        </Typography>
      )}

      <div className="user-setup-field flex justify-end">
        {isLoading ? (
          <div className="w-full flex justify-center">
            <Spinner />
          </div>
        ) : (
          <button
            className="button button-small button-block button-yellow"
            disabled={isBtnDisabled()}
            onClick={submit}
          >
            Save Changes
          </button>
        )}
      </div>
    </>
  );
};

export const OnAccountForm = ({
  user,
  onAccount,
  onSubmit,
  zIndex,
}: {
  user: User;
  onAccount?: OnAccount;
  onSubmit?: () => void;
  zIndex?: number;
}) => {
  const { updateUserBankDetails, generateUserOnAccount } = useAPI();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accountNumber, setAccountNumber] = useState<string>('');

  const isBtnDisabled = (): boolean => accountNumber === '';

  const handleUpdate = () => {
    setIsLoading(true);

    updateUserBankDetails({
      userId: user.id,
      accountNumber,
      status: onAccount?.status || OnAccountStatus.Pending,
    })
      .then(() => {
        toast('On account info has been updated successfully', 'success');
        onSubmit?.();
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error updating user account';
        toast(errMsg, 'error');
      })
      .finally(() => setIsLoading(false));
  };

  const handleGenerate = () => {
    if (isLoading) return;

    setIsLoading(true);
    generateUserOnAccount(user.id)
      .then(() => {
        toast('Account has been generated successfully', 'success');
        setIsModalOpen(false);
        onSubmit?.();
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error generating user account';
        toast(errMsg, 'error');
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setAccountNumber(onAccount?.accountNumber || '');
  }, [onAccount, user]);

  return onAccount ? (
    <>
      <Field
        type="text"
        placeholder="28"
        label="Account number"
        value={accountNumber}
        onChange={(newValue: string) => setAccountNumber(newValue)}
        isRequired={true}
      />
      <div className="user-setup-field flex justify-end">
        {isLoading ? (
          <div className="w-full flex justify-center">
            <Spinner />
          </div>
        ) : (
          <button
            className="button button-small button-block button-yellow"
            disabled={isBtnDisabled()}
            onClick={handleUpdate}
          >
            Save Changes
          </button>
        )}
      </div>
    </>
  ) : (
    <div>
      <Button className="button-primary" onClick={() => setIsModalOpen(true)}>
        Generate Account
      </Button>

      <ConfirmModal
        isOpen={isModalOpen}
        prompt="Generate Account"
        additionalText={
          <>
            You are about to generate an account for this user.
            <br /> Are you sure you want to proceed?
          </>
        }
        noLabel="Cancel"
        yesLabel="Generate"
        yesButtonYellow
        close={() => !isLoading && setIsModalOpen(false)}
        onConfirm={handleGenerate}
        zIndex={zIndex}
      />
    </div>
  );
};
